import EntryGrid from "@/components/EntryGrid";
import SplitViewLayout from "@/components/SplitViewLayout";
import { useAdContext } from "@/contexts/AdContext";
import { useHeaderContext } from "@/contexts/HeaderContext";
import getCommonStaticProps from "@/lib/getCommonStaticProps";
import createBreadcrumbsJsonLd from "@/src/recipes/lib/createBreadcrumbsJsonLd";
import clsx from "classnames";
import craft from "lib/craft";
import Head from "next/head";
import QueryMealPlanCollection from "queries/QueryMealPlanCollection.graphql";
import QueryMealPlanCollectionsIndex from "queries/QueryMealPlanCollectionsIndex.graphql";
import QuerySlugs from "queries/QuerySlugs.graphql";
import { useEffect } from "react";
import { Seomatic } from "react-seomatic";

export async function getStaticPaths() {
  const response = await craft([
    {
      document: QuerySlugs,
      variables: {
        section: "mealPlanCollections",
        limit: process.env.VERCEL_ENV !== "production" ? 1 : 1,
      },
    },
  ]);

  const { entries } = response[0] || {};

  const paths = entries.map((entry) => ({
    params: { slug: entry.slug },
  }));

  return { paths, fallback: "blocking" };
}

export async function getStaticProps({ params, previewData, preview }) {
  // Fetch common static properties
  const commonStaticProps = await getCommonStaticProps();

  const { slug } = params;

  const queries = [
    {
      document: QueryMealPlanCollectionsIndex,
    },
    {
      document: QueryMealPlanCollection,
      variables: {
        slug,
      },
    },
  ];

  const response = await craft(queries, preview ? previewData.token : null);

  const { pageEntry } = response[0];
  const { mealPlanCollection } = response[1];

  const breadcrumbs = [
    { label: "Meal Plans", href: "/meal-plans" },
    { label: "Meal Plan Collection", href: "/meal-plans/collections" },
    { label: mealPlanCollection.title, href: null },
  ];

  if (!mealPlanCollection) {
    return {
      props: {
        ...commonStaticProps,
      },
      notFound: true,
    };
  }

  const jsonLdSchema = [createBreadcrumbsJsonLd(breadcrumbs)];

  return {
    props: {
      ...commonStaticProps,
      pageEntry,
      mealPlanCollection,
      breadcrumbs,
      seomatic: mealPlanCollection.seomatic,
      jsonLdSchema,
    },
    revalidate: process.env.VERCEL_ENV !== "production" ? 1 : 3600,
  };
}

const MealPlanCollectionEntry = ({ mealPlanCollection, pageEntry }) => {
  const { mealPlans } = mealPlanCollection || {};

  // Set showAds to true for this page
  const { setShowAds } = useAdContext();
  useEffect(() => setShowAds(true));

  // Show the header border for this page
  const { setShowBorder } = useHeaderContext();
  useEffect(() => {
    setShowBorder(true);
    return () => setShowBorder(false);
  }, []);

  const heroImage = mealPlanCollection.heroImage?.[0];

  return (
    <>
      <Seomatic Head={Head} {...mealPlanCollection.seomatic} />

      <div className={clsx("border-b")}>
        <SplitViewLayout
          heading={mealPlanCollection.title}
          introduction={mealPlanCollection.summary}
          images={[heroImage]}
        />
      </div>

      <div className={clsx("border-b px-6 py-6 lg:px-12 lg:py-12")}>
        <EntryGrid entries={mealPlans} />
      </div>
    </>
  );
};

export default MealPlanCollectionEntry;
